import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import { Link } from 'react-router-dom'
import { AdminEventJourneyDto, AdminVenueEventDto, ApiError } from '@ivy-monorepo/shared'
import { BaseModalProps } from '../../types/modal'
import { adminService } from '../../services/admin.service'
import { formatTicketPrice } from '../../utils/ticketPrice'
import { formatEventStatus, formatJourneyDate } from '../../utils/eventJourney'
import EventStatTiles from '../EventStatTiles'
import EventJourneySummary from '../EventJourneySummary'

type EventDetailModalProps = BaseModalProps & {
  event: AdminVenueEventDto | null
}

/** Everything about one event that doesn't fit the events table, plus its engagement and journey stats. */
const EventDetailModal = ({ isOpen, onClose, event }: EventDetailModalProps) => {
  const [journey, setJourney] = useState<AdminEventJourneyDto | null>(null)
  const [journeyError, setJourneyError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !event) return
    setJourney(null)
    setJourneyError(null)
    adminService
      .getEventJourney(event.publicId)
      .then(setJourney)
      .catch((err: ApiError) => setJourneyError(err.friendlyMessage || err.message || 'Failed to load stats.'))
  }, [isOpen, event])

  if (!event) return null

  const recurring = event.dayOfWeek
    ? `Every ${event.dayOfWeek}${event.startDate ? ` from ${formatJourneyDate(event.startDate)}` : ''}${
        event.endDate ? ` until ${formatJourneyDate(event.endDate)}` : ''
      }`
    : null

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      className="modal-popup event-detail-modal"
      overlayClassName="modal-overlay"
      contentLabel="Event details"
    >
      <div className="modal-content-wrapper">
        <div className="modal-body-content">
          <div className="event-detail-modal__header">
            {event.flyerUrl && <div className="event-detail-modal__flyer" style={{ backgroundImage: `url(${event.flyerUrl})` }} />}
            <div>
              <div className="event-detail-modal__title">
                <h3>{event.name}</h3>
                {event.isTicketed && (
                  <span className="event-ticketed-badge" title="Ticketed (paid) event">
                    Ticket · {formatTicketPrice(event.ticketPriceCents)}
                  </span>
                )}
              </div>
              <p className="event-detail-modal__subtitle">
                <Link to={`/admin/venues/${event.venue.publicId}`}>{event.venue.name}</Link>
                {event.venue.address ? ` · ${event.venue.address}` : ''}
              </p>
            </div>
          </div>

          <section className="event-journey-section">
            <h4>Details</h4>
            <dl>
              <div>
                <dt>Starts</dt>
                <dd>{formatJourneyDate(event.eventStartDate)}</dd>
              </div>
              <div>
                <dt>Ends</dt>
                <dd>{formatJourneyDate(event.eventEndDate)}</dd>
              </div>
              {recurring && (
                <div>
                  <dt>Recurring</dt>
                  <dd>{recurring}</dd>
                </div>
              )}
              <div>
                <dt>Status</dt>
                <dd>{formatEventStatus(event.isActive, event.visibility)}</dd>
              </div>
              {(event.eventAddress || event.hideAddress) && (
                <div>
                  <dt>Address</dt>
                  <dd>
                    {event.eventAddress || event.venue.address || '—'}
                    {event.hideAddress ? ' (hidden in the app)' : ''}
                  </dd>
                </div>
              )}
              {event.externalLink?.link && (
                <div>
                  <dt>Link</dt>
                  <dd>
                    <a href={event.externalLink.link} target="_blank" rel="noreferrer">
                      {event.externalLink.name || event.externalLink.link}
                    </a>
                  </dd>
                </div>
              )}
              <div>
                <dt>Created</dt>
                <dd>{formatJourneyDate(event.createdAt)}</dd>
              </div>
              {event.content && (
                <div>
                  <dt>Description</dt>
                  <dd className="event-detail-modal__content">{event.content}</dd>
                </div>
              )}
            </dl>
          </section>

          <section className="event-journey-section">
            <h4>Engagement</h4>
            <EventStatTiles
              tiles={[
                { label: 'RSVPs', value: event.rsvpedCount },
                { label: 'Likes', value: event.likeCount },
                { label: 'Comments', value: event.commentCount },
              ]}
            />
          </section>

          <section className="event-journey-section">
            <h4>How users got here</h4>
            {journeyError && <div className="page-error">{journeyError}</div>}
            {!journey && !journeyError && <div className="page-loading">Loading…</div>}
            {journey && <EventJourneySummary summary={journey} />}
          </section>
        </div>

        <div className="modal-footer-content">
          <Link className="styled-button event-detail-modal__link" to={`/admin/venue-events/${event.publicId}/journey`} onClick={onClose}>
            View user journeys
          </Link>
          <Link className="styled-button" to={`/admin/venues/${event.venue.publicId}`} onClick={onClose}>
            Open venue
          </Link>
          <button type="button" className="styled-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default EventDetailModal
